import React, { useState, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, TextField, Stepper, Step, StepLabel,
  IconButton, Alert, CircularProgress, Divider, Checkbox, FormControlLabel, Grid
} from '@mui/material';
import {
  Close as CloseIcon, CalendarMonth as CalendarIcon,
  Store as StoreIcon, Inventory as ServiceIcon,
  Engineering as WorkerIcon, Rocket as RocketIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useEmpresa } from '../../context/EmpresaContext';
import config from '../../config';

const steps = [
  { label: 'Período', icon: <CalendarIcon />, desc: 'Define el período contable activo' },
  { label: 'Local', icon: <StoreIcon />, desc: 'Registra tu sede o local' },
  { label: 'Servicio', icon: <ServiceIcon />, desc: 'Agrega al menos un servicio' },
  { label: 'Trabajador', icon: <WorkerIcon />, desc: 'Registra a tu primer técnico' },
];

export default function WizardConfiguracion({ open, onClose, onComplete }) {
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);

  // Form states
  const now = new Date();
  const [periodo, setPeriodo] = useState({ anio: now.getFullYear(), mes: now.getMonth() + 1, contable: true });
  const [local, setLocal] = useState({ nombre_local: '', direccion_local: '', telefono_local: '' });
  const [servicio, setServicio] = useState({ nombre_servicio: '', descripcion_servicio: '', precio_base: '' });
  const [trabajador, setTrabajador] = useState({
    nombre_trabajador: '', apellido_trabajador: '', email_trabajador: '',
    telefono_trabajador: '', tipo_documento: 'DNI', numero_documento: '',
  });

  const headers = { Authorization: `Bearer ${user?.token}` };

  const handleNext = async () => {
    setError('');
    setLoading(true);

    try {
      if (activeStep === 0) {
        // Crear período
        if (!periodo.anio || !periodo.mes) throw new Error('Año y mes son obligatorios');
        await axios.post(`${config.API_BASE_URL}/api/periodos`, periodo, { headers });
      } else if (activeStep === 1) {
        // Crear local
        if (!local.nombre_local) throw new Error('Nombre del local es obligatorio');
        await axios.post(`${config.API_BASE_URL}/api/locales`, local, { headers });
      } else if (activeStep === 2) {
        // Crear servicio
        if (!servicio.nombre_servicio) throw new Error('Nombre del servicio es obligatorio');
        await axios.post(`${config.API_BASE_URL}/api/servicios`, servicio, { headers });
      } else if (activeStep === 3) {
        // Crear trabajador
        if (!trabajador.nombre_trabajador || !trabajador.email_trabajador) {
          throw new Error('Nombre y email son obligatorios');
        }
        await axios.post(`${config.API_BASE_URL}/api/trabajadores`, trabajador, { headers });
      }

      if (activeStep < steps.length - 1) {
        setActiveStep(prev => prev + 1);
      } else {
        setCompleted(true);
        localStorage.setItem('wizard_completado', 'true');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleFinish = () => {
    localStorage.setItem('wizard_completado', 'true');
    if (onComplete) onComplete();
    onClose();
  };

  const handleCloseWizard = () => {
    localStorage.setItem('wizard_completado', 'true');
    onClose();
  };

  if (completed) {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <RocketIcon sx={{ fontSize: 64, color: '#10B981', mb: 2 }} />
          <Typography variant="h5" fontWeight={800} color="#1E2783" gutterBottom>
            ¡Todo listo!
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Tu empresa está configurada. Ya puedes generar tu primera orden de servicio.
          </Typography>
          <Button
            variant="contained" size="large"
            onClick={handleFinish}
            sx={{ bgcolor: '#1E2783', borderRadius: 2, px: 4, fontWeight: 700 }}
          >
            Crear mi primera orden
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ borderBottom: '1px solid #E2E8F0', pb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.5}>
            <RocketIcon sx={{ color: '#1E2783' }} />
            <Box>
              <Typography variant="h6" fontWeight={700}>Configura tu empresa</Typography>
              <Typography variant="caption" color="text.secondary">
                Completa estos pasos para empezar a usar TRACKEA
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseWizard} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((step, i) => (
            <Step key={step.label} completed={i < activeStep}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Paso 0: Período */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="#1E2783" gutterBottom>
              {steps[0].desc}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              El período contable define el mes y año en el que se registran tus órdenes de servicio.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth label="Año *" type="number" value={periodo.anio}
                  onChange={(e) => setPeriodo({ ...periodo, anio: parseInt(e.target.value) })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Mes *" type="number" value={periodo.mes}
                  inputProps={{ min: 1, max: 12 }}
                  onChange={(e) => setPeriodo({ ...periodo, mes: parseInt(e.target.value) })} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={periodo.contable} onChange={(e) => setPeriodo({ ...periodo, contable: e.target.checked })} />}
                  label="Período contable activo (se usará por defecto en las órdenes)"
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Paso 1: Local */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="#1E2783" gutterBottom>
              {steps[1].desc}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              El local es donde tu equipo realiza los servicios o desde donde salen a campo.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Nombre del local *" value={local.nombre_local}
                  placeholder="Ej: Sede Central, Local San Isidro"
                  onChange={(e) => setLocal({ ...local, nombre_local: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Dirección" value={local.direccion_local}
                  onChange={(e) => setLocal({ ...local, direccion_local: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Teléfono" value={local.telefono_local}
                  onChange={(e) => setLocal({ ...local, telefono_local: e.target.value })} />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Paso 2: Servicio */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="#1E2783" gutterBottom>
              {steps[2].desc}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Los servicios son lo que tu empresa ofrece. Puedes agregar más después.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Nombre del servicio *" value={servicio.nombre_servicio}
                  placeholder="Ej: Corte de cabello, Limpieza profunda, Reparación lavadora"
                  onChange={(e) => setServicio({ ...servicio, nombre_servicio: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Descripción" value={servicio.descripcion_servicio}
                  multiline rows={2}
                  onChange={(e) => setServicio({ ...servicio, descripcion_servicio: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label={`Precio base (${simboloMoneda || 'S/'})`} type="number" value={servicio.precio_base}
                  onChange={(e) => setServicio({ ...servicio, precio_base: e.target.value })} />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Paso 3: Trabajador */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="#1E2783" gutterBottom>
              {steps[3].desc}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Este será el primer técnico/trabajador de tu equipo. Recibirá los servicios en la app móvil.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth label="Nombres *" value={trabajador.nombre_trabajador}
                  onChange={(e) => setTrabajador({ ...trabajador, nombre_trabajador: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Apellidos *" value={trabajador.apellido_trabajador}
                  onChange={(e) => setTrabajador({ ...trabajador, apellido_trabajador: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Email *" type="email" value={trabajador.email_trabajador}
                  helperText="Con este email accederá a la app móvil"
                  onChange={(e) => setTrabajador({ ...trabajador, email_trabajador: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Teléfono" value={trabajador.telefono_trabajador}
                  onChange={(e) => setTrabajador({ ...trabajador, telefono_trabajador: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Tipo documento" value={trabajador.tipo_documento}
                  onChange={(e) => setTrabajador({ ...trabajador, tipo_documento: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Nro documento" value={trabajador.numero_documento}
                  onChange={(e) => setTrabajador({ ...trabajador, numero_documento: e.target.value })} />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #E2E8F0', px: 3, py: 2 }}>
        <Button onClick={handleCloseWizard} color="inherit" size="small">
          Cerrar y configurar después
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={() => setActiveStep(prev => prev - 1)} disabled={loading}>Atrás</Button>
        )}
        <Button onClick={handleSkip} disabled={loading} color="inherit" size="small">
          Omitir
        </Button>
        <Button
          variant="contained" onClick={handleNext} disabled={loading}
          startIcon={loading ? <CircularProgress size={18} /> : (activeStep === steps.length - 1 ? <CheckIcon /> : null)}
          sx={{ bgcolor: '#1E2783', '&:hover': { bgcolor: '#0D47A1' }, borderRadius: 2, fontWeight: 600 }}
        >
          {loading ? 'Guardando...' : activeStep === steps.length - 1 ? 'Finalizar' : 'Guardar y continuar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
